import { addDays } from 'date-fns';
import { Casa, Tarea, TareaHecha } from '../types';
import { claveDia, describirDia, mediodia, ocurrencias } from './tareas';

/**
 * El reparto de una casa en palabras: quién ha hecho cuánto, si va
 * equilibrado y qué le toca a cada uno la semana que viene. Lo usan la
 * tarjeta «Cómo va el reparto» de la semana y el resumen del domingo que
 * manda el servidor (resumen-tareas), así que las dos dicen lo mismo.
 * Funciones puras (repartoTareas.test.ts).
 */

export interface RepartoPersona {
  id: string;
  nombre: string;
  /** Tareas hechas (no saltadas). */
  hechas: number;
  /** Lo mismo pesado por el esfuerzo (1, 2 o 3 cada una). */
  puntos: number;
  /** Las que le tocan los próximos 7 días. */
  proximas: number;
}

export interface Reparto {
  personas: RepartoPersona[];
  hechas: number;
  puntos: number;
  /** De los próximos 7 días, las que son de cualquiera. */
  proximasDeCualquiera: number;
  frases: string[];
}

/** «a», «a y b», «a, b y c». */
export const enLista = (cosas: string[]) =>
  cosas.length <= 1 ? (cosas[0] ?? '') : `${cosas.slice(0, -1).join(', ')} y ${cosas[cosas.length - 1]}`;

const tareasN = (n: number) => (n === 1 ? '1 tarea' : `${n} tareas`);

/** «hoy», «mañana», «el jueves 6», «el 6 de noviembre», «hace 3 días». */
export function paraCuando(fecha: Date, hoy: Date = new Date()): string {
  const d = describirDia(fecha, hoy);
  if (/^\d/.test(d)) return `el ${d}`;
  return d[0].toLowerCase() + d.slice(1);
}

/**
 * Cuánto ha hecho cada uno entre `desde` y `hasta` (incluidos) y lo que le
 * toca en los 7 días desde `hoy`.
 */
export function resumenReparto(
  casa: Casa, tareas: Tarea[], hechas: TareaHecha[], desde: Date, hasta: Date, hoy: Date = new Date(),
): Reparto {
  const ini = claveDia(desde);
  const fin = claveDia(hasta);
  const deCasa = tareas.filter(t => t.casaId === casa.id);
  const esfuerzo = new Map(deCasa.map(t => [t.id, t.esfuerzo]));

  const porId = new Map<string, RepartoPersona>();
  casa.participantes.filter(p => p.activo)
    .forEach(p => porId.set(p.id, { id: p.id, nombre: p.nombre, hechas: 0, puntos: 0, proximas: 0 }));
  const persona = (id: string) => {
    if (!porId.has(id)) {
      const p = casa.participantes.find(x => x.id === id);
      if (!p) return undefined;
      porId.set(id, { id, nombre: p.nombre, hechas: 0, puntos: 0, proximas: 0 });
    }
    return porId.get(id);
  };

  let total = 0, puntos = 0;
  for (const h of hechas) {
    const k = claveDia(h.hechaEn);
    if (h.casaId !== casa.id || h.saltada || k < ini || k > fin) continue;
    const pts = esfuerzo.get(h.tareaId) ?? 1;
    total++;
    puntos += pts;
    const p = h.participanteId ? persona(h.participanteId) : undefined;
    if (p) { p.hechas++; p.puntos += pts; }
  }

  let proximasDeCualquiera = 0;
  const hoyM = mediodia(hoy);
  for (const o of ocurrencias(deCasa, [], hoyM, addDays(hoyM, 6), hoyM)) {
    const p = o.tarea.asignadoA ? persona(o.tarea.asignadoA) : undefined;
    if (p) p.proximas++;
    else proximasDeCualquiera++;
  }

  const personas = Array.from(porId.values())
    .sort((a, b) => b.puntos - a.puntos || b.hechas - a.hechas || a.nombre.localeCompare(b.nombre, 'es'));
  return { personas, hechas: total, puntos, proximasDeCualquiera, frases: frasesReparto(personas, total, puntos, proximasDeCualquiera, deCasa, casa, hoyM) };
}

function frasesReparto(
  personas: RepartoPersona[], total: number, puntos: number, deCualquiera: number,
  tareas: Tarea[], casa: Casa, hoy: Date,
): string[] {
  const frases: string[] = [];

  // Lo hecho
  if (total === 0) {
    frases.push('Esta semana no se ha marcado ninguna tarea como hecha.');
  } else if (personas.length <= 1) {
    frases.push(`Esta semana se han hecho ${tareasN(total)}.`);
  } else {
    const [primera, ...resto] = personas;
    const otros = resto.map(p => `${p.nombre} ${p.hechas === 0 ? 'ninguna' : p.hechas}`);
    frases.push(`Esta semana ${primera.nombre} ha hecho ${tareasN(primera.hechas)}${otros.length ? `, ${enLista(otros)}` : ''}.`);
  }

  // Si va equilibrado: por esfuerzo, no por número, que fregar no es limpiar el horno
  if (personas.length >= 2 && puntos >= 4) {
    const justo = 1 / personas.length;
    const [mas] = personas;
    const parte = mas.puntos / puntos;
    const menos = personas[personas.length - 1];
    if (parte - justo >= 0.15) {
      frases.push(`${mas.nombre} se ha llevado el ${Math.round(parte * 100)} % del esfuerzo. La semana que viene, ` +
        `${menos.nombre} podría coger alguna de más para equilibrarlo.`);
    } else {
      frases.push('El reparto va equilibrado: nadie se ha llevado mucho más trabajo que los demás.');
    }
  }

  // Lo que viene
  const conProximas = personas.filter(p => p.proximas > 0).sort((a, b) => b.proximas - a.proximas);
  if (conProximas.length || deCualquiera) {
    const partes = conProximas.map((p, i) => (i === 0 ? `a ${p.nombre} le ${p.proximas === 1 ? 'toca 1' : `tocan ${p.proximas}`}` : `a ${p.nombre} ${p.proximas}`));
    const cualquiera = deCualquiera ? `${deCualquiera === 1 ? '1 es' : `${deCualquiera} son`} de cualquiera` : '';
    frases.push(partes.length
      ? `Los próximos 7 días ${enLista(partes)}${cualquiera ? `; ${cualquiera}` : ''}.`
      : `Los próximos 7 días hay ${tareasN(deCualquiera)}, todas de cualquiera.`);
  }

  // Turnos: a quién le toca ahora
  const turnos = tareas.filter(t => t.activa && t.turno && t.asignadoA).slice(0, 3)
    .map(t => {
      const p = casa.participantes.find(x => x.id === t.asignadoA);
      return p ? `«${t.nombre}» le toca a ${p.nombre} (${paraCuando(t.fecha, hoy)})` : '';
    }).filter(Boolean);
  if (turnos.length) frases.push(`Por turnos, ${enLista(turnos)}.`);

  return frases;
}

/** El resumen del domingo para una persona: lo de la casa y lo suyo. */
export function textoResumenPara(reparto: Reparto, participanteId: string | undefined): string {
  const yo = reparto.personas.find(p => p.id === participanteId);
  const partes = [reparto.frases[0]];
  if (yo && reparto.personas.length > 1 && reparto.hechas > 0) {
    partes.push(yo.hechas ? `Tú has hecho ${tareasN(yo.hechas)}.` : 'Tú no has marcado ninguna.');
  }
  if (yo) {
    partes.push(yo.proximas
      ? `La semana que viene te ${yo.proximas === 1 ? 'toca 1' : `tocan ${yo.proximas}`}.`
      : 'La semana que viene no tienes ninguna asignada.');
  }
  return partes.join(' ');
}

export interface EstrellasPeque {
  id: string;
  nombre: string;
  /** Estrellas (el esfuerzo de cada tarea hecha) de la semana y del mes. */
  semana: number;
  mes: number;
  /** Lo último que ha hecho esta semana, para celebrarlo. */
  ultimas: string[];
}

/**
 * Las estrellas de los peques de la casa: cada tarea hecha suma su esfuerzo
 * (1, 2 o 3). La semana va de lunes a hoy; el mes, desde el día 1.
 */
export function estrellasPeques(
  casa: Casa, tareas: Tarea[], hechas: TareaHecha[], lunes: Date, hoy: Date = new Date(),
): EstrellasPeque[] {
  const peques = casa.participantes.filter(p => p.peque && p.activo);
  if (!peques.length) return [];
  const esfuerzo = new Map(tareas.filter(t => t.casaId === casa.id).map(t => [t.id, t.esfuerzo]));
  const fin = claveDia(hoy);
  const iniSemana = claveDia(lunes);
  const iniMes = claveDia(new Date(hoy.getFullYear(), hoy.getMonth(), 1, 12));
  return peques.map(p => {
    const suyas = hechas
      .filter(h => h.casaId === casa.id && !h.saltada && h.participanteId === p.id && claveDia(h.hechaEn) <= fin)
      .sort((a, b) => b.hechaEn.getTime() - a.hechaEn.getTime());
    const suma = (desde: string) => suyas.filter(h => claveDia(h.hechaEn) >= desde)
      .reduce((s, h) => s + (esfuerzo.get(h.tareaId) ?? 1), 0);
    return {
      id: p.id, nombre: p.nombre, semana: suma(iniSemana), mes: suma(iniMes),
      ultimas: suyas.filter(h => claveDia(h.hechaEn) >= iniSemana).slice(0, 3).map(h => `${h.icono} ${h.nombreTarea}`),
    };
  });
}
